import { Component, Input, DestroyRef, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { Constants } from 'src/app/models/constants';
import { LocalConfiguration } from 'src/app/services/api/local-config.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { UrlService } from 'src/app/services/util/url.service';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-environment-apply-local-config',
  templateUrl: './environemnt-apply-local-config.component.html',
  styleUrl: './environemnt-apply-local-config.component.scss',
  standalone: false
})

export class EnvironmentApplyLocalConfigComponent {
  @Input() data: any;
  @Input() fetchMigrateFailed: boolean = false;
  @Input() isAWS: boolean = false;
  private destroyRef = inject(DestroyRef);
  isMigrating: boolean = false;
  tooltipVisible: boolean = false;
  isMigrationFailed: boolean = false;
  showMigrateLoader: boolean = false;
  disableMigrate:boolean = true;
  Constants = Constants;
  DeployStatus =  DeployStatus;
  readonly INSTANCE_DEPLOYING_STATES = [DeployStatus.INITIALIZED, DeployStatus.INITIALIZING, DeployStatus.INITIALIZE_FAILED,
    DeployStatus.DEPLOYING, DeployStatus.TERMINATED, DeployStatus.TERMINATION_FAILED, DeployStatus.TERMINATING,
    DeployStatus.CONFIGURING, DeployStatus.UPDATING, DeployStatus.TESTING, DeployStatus.FAILED,
    DeployStatus.FAILOVER_APPLYING, DeployStatus.FAILBACK_APPLYING,
    DeployStatus.PRE_FAILBACK_APPLYING, DeployStatus.POST_FAILBACK_APPLYING,
    DeployStatus.OPERATION_INPROGRESS, DeployStatus.TENANT_AGENT_CREATION_IN_PROGRESS, DeployStatus.START_FAILED, DeployStatus.STOP_FAILED, DeployStatus.STARTING,
    DeployStatus.STOPPING, DeployStatus.STOPPED, DeployStatus.STARTING_AGENT, DeployStatus.STOPPING_AGENT, DeployStatus.INPROGRESS];
    

  constructor(private dialog: MatDialog, public urlService: UrlService, private localConfigService: LocalConfiguration, public toastrService: CustomToastrService) { }

  ngOnInit() {
    this.updateFlags();
  }

  //Updates the migrate status flags based on status received via API response
   private updateFlags() {
    if (!this.data?.id) return;
    this.showMigrateLoader = true;
    this.localConfigService.getLocalConfigurationInit(this.data.id)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          this.applyStatus(res?.status);
        },
         error: (error) => {
          this.isMigrating = false;
          setTimeout(() => {
          const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
        }
      });
  }

  /**
   * Maps init status to UI flags.
   * Central place to control all migration-related states.
   */
  private applyStatus(status: string) {
    this.isMigrating = status === DeployStatus.INITIALIZING;
    this.isMigrationFailed = status === DeployStatus.INITIALIZE_FAILED;
    this.showMigrateLoader = false;
    if (!this.INSTANCE_DEPLOYING_STATES.includes(this.data?.status)) {
      this.disableMigrate = false;
    }
  }

  /**
 * Opens confirmation dialog and initiates local config migration.
 * On success, marks migration in progress and shows a toast.
 * Handles errors via error dialog.
 */
  confirmMigrate() {
    const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '650px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_SUMMARY.UPDATE_CONFIGURATION.MIGRATE_LOCAL_CONFIG'),
        message: translate('INSTANCE_SUMMARY.UPDATE_CONFIGURATION.MIGRATE_CONFIRMATION'),
        note: translate('INSTANCE_SUMMARY.UPDATE_CONFIGURATION.MIGRATE_NOTE_SHARED_INFRA'),
        noteTextColor: true,
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    });
    confirmDialog.afterClosed().subscribe(confirm => {
      if (confirm) {
        this.showMigrateLoader = true;
        this.localConfigService.putLocalConfigurationInit(this.data.id)
          .subscribe({
            next: data => {
              if (data?.httpStatusCode === 202) {
                this.showMigrateLoader = false;
                this.isMigrating = true;
                this.isMigrationFailed = false;
                setTimeout(() => {
                  const title = translate('INSTANCE_SUMMARY.UPDATE_CONFIGURATION.MIGRATE_LOCAL_CONFIG');
                  const message = translate('INSTANCE_SUMMARY.UPDATE_CONFIGURATION.MIGRATE_LOCAL_CONFIG_SUCCESS');
                  this.toastrService.successMessage(message, title);
                }, 500);
              }
            },
            error: error => {
              this.showMigrateLoader = false; // stop loader on error
              this.openErrorDialog(error);
            }
          });
      }
    });
  }

  // Handles errors via error dialog.
  openErrorDialog(error: any): void {
    this.dialog.open(ErrorDialogComponent, {
      minHeight: '342px',
      width: '630px',
      panelClass: 'flex-dialog',
      data: {
        title: translate('BLUEPRINT_SUMMARY.VALIDATION_ERROR'),
        descriptionError: error
      }
    });
  }

  //Navigates to documentation URL based on current host
  openUrl() {
    let URL = `${window.location.protocol}//${window.location.hostname}/userguide/docs/tenant-view/configure-the-environments/apply-local-configuration`
    this.urlService.openUrlInNewTab(URL);
  }
}